import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Layers, TrendingUp, Package } from "lucide-react";

export default function InventoryValuation() {
    const [method, setMethod] = useState("fifo");
    const [data, setData] = useState(null);
    const [drill, setDrill] = useState({ open: false, item: null, detail: null, loading: false });

    const load = async () => {
        const { data } = await api.get("/inventory/valuation", { params: { method } });
        setData(data);
    };

    useEffect(() => { load(); }, [method]);

    const openDrill = async (row) => {
        setDrill({ open: true, item: row, detail: null, loading: true });
        const { data } = await api.get(`/inventory/items/${row.item_id}/movements`, { params: { method } });
        setDrill({ open: true, item: row, detail: data, loading: false });
    };

    return (
        <>
            <PageHeader
                kicker="REPORTS • INVENTORY"
                title="Inventory Valuation"
                subtitle={`Closing stock value & COGS using ${method === "fifo" ? "First-In-First-Out" : "Weighted-Average"} method.`}
                actions={
                    <>
                        <div className="inline-flex border border-border rounded-md p-0.5 bg-card">
                            {[
                                { v: "fifo", label: "FIFO" },
                                { v: "weighted_avg", label: "Weighted Avg" },
                            ].map((m) => (
                                <button
                                    key={m.v}
                                    onClick={() => setMethod(m.v)}
                                    data-testid={`valuation-method-${m.v}`}
                                    className={`text-[12px] font-medium px-3 py-1.5 rounded ${method === m.v ? "bg-[#13192e] text-white" : "text-muted-foreground hover:text-foreground"}`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        {data && (
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Total stock value</div>
                                <div className="serif-num text-2xl">{fmtMoney(data.total_value)}</div>
                            </div>
                        )}
                    </>
                }
            />
            <PageBody>
                {data && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <SummaryCard icon={Package} label="Items tracked" value={String(data.rows.length)} />
                        <SummaryCard icon={Layers} label="Closing value" value={fmtMoney(data.total_value)} />
                        <SummaryCard icon={TrendingUp} label="Cumulative COGS" value={fmtMoney(data.total_cogs)} />
                    </div>
                )}
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {data && (
                        <table className="fin-table">
                            <thead><tr><th>Item</th><th>SKU</th><th className="num">Qty on hand</th><th className="num">Avg cost</th><th className="num">Stock value</th><th className="num">COGS</th></tr></thead>
                            <tbody>
                                {data.rows.map((r) => (
                                    <tr key={r.item_id} className="cursor-pointer hover:bg-[#fafaf7]" onClick={() => openDrill(r)} data-testid={`val-row-${r.item_id}`}>
                                        <td className="font-medium">{r.name}</td>
                                        <td className="font-mono text-[12px]">{r.sku || "—"}</td>
                                        <td className="num">{r.qty_on_hand} {r.unit}</td>
                                        <td className="num">{fmtMoney(r.avg_cost)}</td>
                                        <td className="num font-medium">{fmtMoney(r.value_on_hand)}</td>
                                        <td className="num text-[12.5px] text-muted-foreground">{fmtMoney(r.cogs)}</td>
                                    </tr>
                                ))}
                                {data.rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No tracked items.</td></tr>}
                            </tbody>
                        </table>
                    )}
                </div>
            </PageBody>

            <Dialog open={drill.open} onOpenChange={(v) => !v && setDrill({ open: false, item: null, detail: null, loading: false })}>
                <DialogContent className="max-w-4xl" data-testid="valuation-drill-dialog">
                    <DialogHeader>
                        <div className="text-[10px] tracking-[0.18em] font-semibold text-[#a68a56]">{method.toUpperCase().replace("_", " ")} STOCK LEDGER</div>
                        <DialogTitle className="text-xl">{drill.item?.name}</DialogTitle>
                    </DialogHeader>
                    {drill.loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading…</div>
                    ) : drill.detail ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Stat label="Qty on hand" value={`${drill.detail.summary.qty_on_hand} ${drill.item?.unit || ""}`} />
                                <Stat label="Avg cost" value={fmtMoney(drill.detail.summary.avg_cost)} />
                                <Stat label="Stock value" value={fmtMoney(drill.detail.summary.value_on_hand)} accent="positive" />
                                <Stat label="Total COGS" value={fmtMoney(drill.detail.summary.cogs)} />
                            </div>
                            {method === "fifo" && (drill.detail.summary.layers || []).length > 0 && (
                                <div>
                                    <div className="text-[10.5px] tracking-[0.16em] font-semibold text-muted-foreground mb-2">REMAINING FIFO LAYERS</div>
                                    <div className="flex flex-wrap gap-2">
                                        {drill.detail.summary.layers.map((l, i) => (
                                            <span key={i} className="border border-border rounded-md px-3 py-1.5 text-[12px] bg-[#fafaf7]">
                                                <b>{l.qty}</b> {drill.item?.unit || ""} @ {fmtMoney(l.rate)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="border border-border rounded-md overflow-hidden max-h-[360px] overflow-y-auto">
                                <table className="fin-table text-[12.5px]">
                                    <thead className="sticky top-0 z-10">
                                        <tr><th>Date</th><th>Source</th><th>Ref</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Cost</th></tr>
                                    </thead>
                                    <tbody>
                                        {drill.detail.movements.map((m, i) => (
                                            <tr key={i}>
                                                <td>{fmtDate(m.date)}</td>
                                                <td>
                                                    <span className={`text-[10.5px] px-1.5 py-0.5 rounded ${m.type === "in" ? "bg-[#1f6c47]/12 text-[#1f6c47]" : "bg-[#b4233a]/12 text-[#b4233a]"}`}>
                                                        {m.type === "in" ? "IN" : "OUT"}
                                                    </span>
                                                    <span className="ml-2 capitalize text-muted-foreground">{m.source}</span>
                                                </td>
                                                <td className="font-mono text-[11.5px]">{m.ref || "—"}</td>
                                                <td className="num">{m.quantity}</td>
                                                <td className="num">{m.type === "in" ? fmtMoney(m.rate) : (m.avg_after !== undefined ? fmtMoney(m.avg_after) : "—")}</td>
                                                <td className="num">{fmtMoney(m.cost || 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </>
    );
}

function SummaryCard({ icon: Icon, label, value }) {
    return (
        <div className="border border-border rounded-md p-4 bg-card flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#a68a56]/10 grid place-items-center">
                <Icon className="w-5 h-5 text-[#a68a56]" />
            </div>
            <div>
                <div className="text-[10.5px] tracking-[0.14em] font-semibold text-muted-foreground">{label.toUpperCase()}</div>
                <div className="text-lg font-semibold">{value}</div>
            </div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    const color = accent === "positive" ? "text-[#1f6c47]" : "text-foreground";
    return (
        <div className="border border-border rounded-md p-3">
            <div className="text-[10.5px] tracking-[0.14em] font-semibold text-muted-foreground">{label.toUpperCase()}</div>
            <div className={`text-base font-semibold mt-0.5 ${color}`}>{value}</div>
        </div>
    );
}
